// @flow

import * as React from 'react';
import { TravelInfo, type TravelInfoData } from '@ta/lithium.context';
import { parseDate, serializeDate } from '@ta/platform.travel-info-parsing';
import AttractionsAccumulator from './attractionsAccumulator';
import FlightsAccumulator from './flightsAccumulator';
import RestaurantsAccumulator from './restaurantsAccumulator';
import parsers from './records';

export type SerializedTravelInfoData = {|
  +attractions: {|
    +singleDate: null | string,
    +fromDate: null | string,
    +toDate: null | string,
    +adults: null | number,
    +children: null | number,
  |},
  +restaurants: {|
    +date: null | string,
  |},
  +hotels: {|
    +stayDates: string,
    +guestInfo: string,
    +defaultDates: boolean,
  |},
  +flights: {|
    +oneWayDate: null | string,
    +departureDate: null | string,
    +returnDate: null | string,
  |},
|};

type Props = {|
  +bootstrap: SerializedTravelInfoData,
  +children: React.Node,
|};

// Visible only for testing.
export class TravelInfoState {
  accumulators = {
    attractions: new AttractionsAccumulator(),
    restaurants: new RestaurantsAccumulator(),
    flights: new FlightsAccumulator(),
  };
  stamp: null | number = null;
  records: Array<string[]>;

  constructor(records: Array<string[]>) {
    this.records = records;
    records.forEach(([key, stamp, content]) => {
      if (key && stamp) {
        this.accumulate(key, parseInt(stamp, 10), content);
      }
    });
  }

  accumulate(key: string, stamp: number, content: string) {
    let currentStamp = stamp;
    if (this.stamp === null) {
      this.stamp = stamp;
    } else {
      currentStamp += this.stamp;
    }
    if (key in parsers) {
      const record = parsers[key](currentStamp, content);
      if (record) {
        Object.values(this.accumulators).forEach(
          // $FlowFixMe: Flow doesn't know instances are indexable.
          accumulator => key in accumulator && accumulator[key].call(accumulator, record),
        );
      }
    }
    return this;
  }

  getTravelInfoData(): $Shape<TravelInfoData> {
    return {
      attractions: {
        fromDate: this.accumulators.attractions.fromDate,
        toDate: this.accumulators.attractions.toDate,
        singleDate: this.accumulators.attractions.singleDate,
        adults: this.accumulators.attractions.adults,
        children: this.accumulators.attractions.children,
      },
      restaurants: {
        date: this.accumulators.restaurants.date,
      },
      flights: {
        oneWayDate: this.accumulators.flights.oneWayDate,
        departureDate: this.accumulators.flights.departureDate,
        returnDate: this.accumulators.flights.returnDate,
      },
    };
  }
}

const readRecords = (): null | string[][] => {
  /* eslint-disable no-restricted-globals */
  const cookie =
    typeof document !== 'undefined'
      ? document.cookie.split(';').find(raw => raw.includes('TAUD='))
      : null;
  /* eslint-enable no-restricted-globals */

  if (!cookie) {
    return null;
  }

  return cookie
    .split('=', 2)[1]
    .split('*')
    .map(record => record.split('-'));
};

const persist = (arg, value, setState) =>
  fetch(`/UpdateSessionDatesAjax?${arg}=${value}`, { credentials: 'same-origin' }).then(() => {
    setState(state => ({
      ...state,
      ...new TravelInfoState(readRecords() || []).getTravelInfoData(),
    }));
  });

const updater = setState => ({
  attractions: {
    setSingleDate: singleDate => {
      persist('attrdate', singleDate === null ? '_' : serializeDate(singleDate), setState);
      setState(state => ({ ...state, attractions: { ...state.attractions, singleDate } }));
    },
    setDates: (fromDate, toDate) => {
      persist('attrrange', `${serializeDate(fromDate)}_${serializeDate(toDate)}`, setState);
      setState(state => ({ ...state, attractions: { ...state.attractions, fromDate, toDate } }));
    },
    setPassengers: (adults, children) => {
      persist('zuf', `${adults}_${children}`, setState);
      setState(state => ({ ...state, attractions: { ...state.attractions, adults, children } }));
    },
  },
  restaurants: {
    setDate: date => {
      persist('eaterydate', date === null ? '_' : serializeDate(date), setState);
      setState(state => ({ ...state, restaurants: { ...state.restaurants, date } }));
    },
  },
  hotels: {
    setStayDates: (checkInDate, checkOutDate) => {
      persist(
        'staydates',
        `${serializeDate(checkInDate)}_${serializeDate(checkOutDate)}`,
        setState,
      );
      setState(state => ({ ...state, hotels: { ...state.hotels, checkInDate, checkOutDate } }));
    },
    setGuestInfo: (adults, rooms, childAgesPerRoom) => {
      persist(
        'uguests',
        `${rooms}_${adults}${
          childAgesPerRoom.length > 0
            ? `_${childAgesPerRoom.map(room => room.join(',')).join(':')}`
            : ''
        }`,
        setState,
      );
      setState(state => ({
        ...state,
        hotels: {
          ...state.hotels,
          adults,
          rooms,
          childAgesPerRoom,
          children: ((childAgesPerRoom: any).flat(): number[]).length,
        },
      }));
    },
  },
  flights: {
    setOneWayDate: oneWayDate => {
      persist('oneway', serializeDate(oneWayDate), setState);
      setState(state => ({ ...state, flights: { ...state.flights, oneWayDate } }));
    },
    setRoundTripDates: (departureDate, returnDate) => {
      persist('rtdates', `${serializeDate(departureDate)}_${serializeDate(returnDate)}`, setState);
      setState(state => ({ ...state, flights: { ...state.flights, departureDate, returnDate } }));
    },
  },
});

const hotelsError = () =>
  new Error('Not implemented: Hotels dates cannot yet be parsed from the TAUD cookie.');

const deserialize = serialized => ({
  attractions: {
    singleDate: parseDate(serialized.attractions.singleDate),
    fromDate: parseDate(serialized.attractions.fromDate),
    toDate: parseDate(serialized.attractions.toDate),
    adults: serialized.attractions.adults,
    children: serialized.attractions.children,
  },
  restaurants: {
    date: parseDate(serialized.restaurants.date),
  },
  hotels: {
    get checkInDate() {
      throw hotelsError();
    },
    get checkOutDate() {
      throw hotelsError();
    },
    get defaultDates() {
      throw hotelsError();
    },
    get rooms() {
      throw hotelsError();
    },
    get adults() {
      throw hotelsError();
    },
    get children() {
      throw hotelsError();
    },
    get childAgesPerRoom() {
      throw hotelsError();
    },
  },
  flights: {
    oneWayDate: parseDate(serialized.flights?.oneWayDate),
    departureDate: parseDate(serialized.flights?.departureDate),
    returnDate: parseDate(serialized.flights?.returnDate),
  },
});

const TravelInfoProvider = ({ bootstrap, children }: Props) => {
  const [travelInfoData, setTravelInfoData] = React.useState<TravelInfoData>(() => {
    const records = readRecords();
    return records === null
      ? deserialize(bootstrap)
      : { ...deserialize(bootstrap), ...new TravelInfoState(records).getTravelInfoData() };
  });

  const update = React.useMemo(() => updater(setTravelInfoData), []);
  const ctx = React.useMemo(
    () => ({
      ...travelInfoData,
      update,
    }),
    [travelInfoData, update],
  );
  return <TravelInfo.Provider value={ctx}>{children}</TravelInfo.Provider>;
};

export default TravelInfoProvider;
